import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { formatCurrency } from "../lib/utils";
import toast from "react-hot-toast";
import axios from "axios";
import {
  Shield,
  Users,
  Crown,
  Clock,
  Ban,
  Search,
  UserCheck,
  CheckCircle,
  XCircle,
  Trash2,
  Palette,
  RefreshCw,
} from "lucide-react";

const API = `${window.location.origin}/api`;
const ADMIN_SECRET = "hesabaty-admin-secret-2024-secure";

export default function SuperAdminPage() {
  const [loading, setLoading] = useState(true);
  const [users, setUsers] = useState([]);
  const [stats, setStats] = useState({});
  const [searchTerm, setSearchTerm] = useState("");
  
  // Activate dialog
  const [isActivateOpen, setIsActivateOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [selectedLicenseType, setSelectedLicenseType] = useState("professional");

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [usersRes, statsRes] = await Promise.all([
        axios.get(`${API}/admin/users?admin_secret=${ADMIN_SECRET}`),
        axios.get(`${API}/admin/stats?admin_secret=${ADMIN_SECRET}`),
      ]);
      setUsers(usersRes.data);
      setStats(statsRes.data);
    } catch (error) {
      console.error("Error fetching admin data:", error);
      toast.error("خطأ في جلب البيانات");
    } finally {
      setLoading(false);
    }
  };

  const openActivateDialog = (user) => {
    setSelectedUser(user);
    setSelectedLicenseType(user.license_type === "regular" ? "regular" : "professional");
    setIsActivateOpen(true);
  };

  const activateUser = async () => {
    if (!selectedUser) return;
    
    try {
      await axios.put(
        `${API}/admin/users/${encodeURIComponent(selectedUser.email)}/activate?license_type=${selectedLicenseType}&admin_secret=${ADMIN_SECRET}`
      );
      toast.success(`تم تفعيل ${selectedUser.email} بنجاح`);
      setIsActivateOpen(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "خطأ في التفعيل");
    }
  };

  const deactivateUser = async (email) => {
    if (!window.confirm(`هل أنت متأكد من إلغاء تفعيل ${email}؟`)) return;
    
    try {
      await axios.put(
        `${API}/admin/users/${encodeURIComponent(email)}/deactivate?admin_secret=${ADMIN_SECRET}`
      );
      toast.success(`تم إلغاء تفعيل ${email}`);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "خطأ في إلغاء التفعيل");
    }
  };

  const deleteUser = async (email) => {
    if (!window.confirm(`هل أنت متأكد من حذف ${email}؟ لا يمكن التراجع عن هذا الإجراء.`)) return;
    
    try {
      await axios.delete(
        `${API}/admin/users/${encodeURIComponent(email)}?admin_secret=${ADMIN_SECRET}`
      );
      toast.success(`تم حذف ${email}`);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "خطأ في الحذف");
    }
  };

  const filteredUsers = users.filter(u => {
    if (!searchTerm) return true;
    const search = searchTerm.toLowerCase();
    return (
      u.name?.toLowerCase().includes(search) ||
      u.email?.toLowerCase().includes(search) ||
      u.phone?.includes(search) ||
      u.shop_name?.toLowerCase().includes(search)
    );
  });

  const getLicenseLabel = (type) => {
    switch (type) {
      case "full": return "الترخيص الكامل";
      case "trial": return "فترة تجريبية";
      case "regular": return "مفعّل";
      case "professional": return "مفعّل";
      case "lifetime": return "مفعّل";
      default: return "بانتظار التفعيل";
    }
  };

  const getLicenseColor = (type, isActive) => {
    if (!isActive) return "bg-slate-500/20 text-slate-400";
    switch (type) {
      case "full": return "bg-emerald-500/20 text-emerald-400";
      case "trial": return "bg-blue-500/20 text-blue-400";
      case "regular": return "bg-emerald-500/20 text-emerald-400";
      case "professional": return "bg-emerald-500/20 text-emerald-400";
      case "lifetime": return "bg-emerald-500/20 text-emerald-400";
      default: return "bg-slate-500/20 text-slate-400";
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="w-8 h-8 border-4 border-amber-600/30 border-t-amber-600 rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in-up" data-testid="super-admin-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="p-3 rounded-xl bg-gradient-to-br from-amber-500 to-orange-600">
            <Shield className="w-8 h-8 text-white" />
          </div>
          <div>
            <h1 className="text-2xl font-heading font-bold text-white">
              لوحة Super Admin
            </h1>
            <p className="text-slate-400">
              إدارة المستخدمين والتراخيص
            </p>
          </div>
        </div>
        <Button
          onClick={fetchData}
          variant="outline"
          className="border-slate-600 text-slate-300"
        >
          <RefreshCw className="w-4 h-4 me-2" />
          تحديث
        </Button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        <Card className="bg-gradient-to-br from-blue-500/20 to-blue-600/10 border-blue-500/30">
          <CardContent className="p-4 flex items-center gap-3">
            <Users className="w-10 h-10 text-blue-500" />
            <div>
              <p className="text-xs text-slate-400">إجمالي المستخدمين</p>
              <p className="text-2xl font-bold text-white">{stats.total_users || 0}</p>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-amber-500/20 to-amber-600/10 border-amber-500/30">
          <CardContent className="p-4 flex items-center gap-3">
            <Clock className="w-10 h-10 text-amber-500" />
            <div>
              <p className="text-xs text-slate-400">بانتظار التفعيل</p>
              <p className="text-2xl font-bold text-white">{stats.pending_users || 0}</p>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-green-500/20 to-green-600/10 border-green-500/30">
          <CardContent className="p-4 flex items-center gap-3">
            <UserCheck className="w-10 h-10 text-green-500" />
            <div>
              <p className="text-xs text-slate-400">مفعّلين</p>
              <p className="text-2xl font-bold text-white">{stats.active_users || 0}</p>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-emerald-500/20 to-emerald-600/10 border-emerald-500/30">
          <CardContent className="p-4 flex items-center gap-3">
            <Crown className="w-10 h-10 text-emerald-500" />
            <div>
              <p className="text-xs text-slate-400">مفعّل</p>
              <p className="text-2xl font-bold text-white">{(stats.regular_users || 0) + (stats.professional_users || 0)}</p>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-blue-500/20 to-blue-600/10 border-blue-500/30">
          <CardContent className="p-4 flex items-center gap-3">
            <Clock className="w-10 h-10 text-blue-500" />
            <div>
              <p className="text-xs text-slate-400">فترة تجريبية</p>
              <p className="text-2xl font-bold text-white">{stats.trial_users || 0}</p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Price Info */}
      <Card className="bg-slate-800/50 border-emerald-500/30">
        <CardContent className="p-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Crown className="w-8 h-8 text-emerald-500" />
            <span className="text-slate-300">الترخيص الكامل</span>
          </div>
          <div className="text-left">
            <span className="text-2xl font-bold text-emerald-400">690,000 د.ع</span>
            <p className="text-xs text-slate-500">دفعة واحدة</p>
          </div>
        </CardContent>
      </Card>

      {/* Users Table */}
      <Card className="bg-slate-800/50 border-slate-700">
        <CardHeader className="pb-3">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <CardTitle className="text-white flex items-center gap-2">
              <Users className="w-5 h-5 text-amber-500" />
              قائمة المستخدمين
            </CardTitle>
            <div className="relative">
              <Search className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                placeholder="بحث..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pr-10 bg-slate-900 border-slate-700 text-white w-64"
              />
            </div>
          </div>
        </CardHeader>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="border-slate-700">
                  <TableHead className="text-amber-500">الاسم</TableHead>
                  <TableHead className="text-amber-500">الإيميل</TableHead>
                  <TableHead className="text-amber-500">المتجر</TableHead>
                  <TableHead className="text-amber-500">الهاتف</TableHead>
                  <TableHead className="text-amber-500">الترخيص</TableHead>
                  <TableHead className="text-amber-500">الإجراءات</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredUsers.map((u) => (
                  <TableRow key={u.id} className="border-slate-700 hover:bg-slate-800/50">
                    <TableCell className="text-white font-medium">{u.name}</TableCell>
                    <TableCell className="text-slate-300">{u.email}</TableCell>
                    <TableCell className="text-slate-300">{u.shop_name || "-"}</TableCell>
                    <TableCell className="text-slate-300 font-mono" dir="ltr">{u.phone || "-"}</TableCell>
                    <TableCell>
                      <span className={`inline-flex items-center gap-1 px-2 py-1 rounded text-xs font-medium ${getLicenseColor(u.license_type, u.is_active)}`}>
                        {u.is_active ? <CheckCircle className="w-3 h-3" /> : <Clock className="w-3 h-3" />}
                        {getLicenseLabel(u.license_type)}
                      </span>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1">
                        {!u.is_active || u.license_type === "none" ? (
                          <Button
                            size="sm"
                            onClick={() => openActivateDialog(u)}
                            className="bg-emerald-600 hover:bg-emerald-700 h-8"
                          >
                            <CheckCircle className="w-3 h-3 me-1" />
                            تفعيل
                          </Button>
                        ) : (
                          <>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => openActivateDialog(u)}
                              className="border-slate-600 text-slate-300 h-8"
                            >
                              تعديل
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => deactivateUser(u.email)}
                              className="border-red-500/50 text-red-400 hover:bg-red-500/20 h-8"
                            >
                              <XCircle className="w-3 h-3" />
                            </Button>
                          </>
                        )}
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => deleteUser(u.email)}
                          className="text-red-400 hover:text-red-300 hover:bg-red-500/20 h-8"
                        >
                          <Trash2 className="w-3 h-3" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      {/* Activate Dialog */}
      <Dialog open={isActivateOpen} onOpenChange={setIsActivateOpen}>
        <DialogContent className="bg-slate-800 border-slate-700 text-white max-w-md">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <CheckCircle className="w-5 h-5 text-emerald-500" />
              تفعيل المستخدم
            </DialogTitle>
          </DialogHeader>

          {selectedUser && (
            <div className="space-y-4">
              <div className="bg-slate-900 rounded-lg p-3 space-y-1">
                <p className="text-slate-300">
                  <span className="text-slate-500">الاسم:</span> {selectedUser.name}
                </p>
                <p className="text-slate-300">
                  <span className="text-slate-500">الإيميل:</span> {selectedUser.email}
                </p>
                <p className="text-slate-300">
                  <span className="text-slate-500">المتجر:</span> {selectedUser.shop_name}
                </p>
              </div>

              <div className="space-y-2">
                <label className="text-slate-200 text-sm">نوع الترخيص</label>
                <Select value={selectedLicenseType} onValueChange={setSelectedLicenseType}>
                  <SelectTrigger className="bg-slate-900 border-slate-700 text-white">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-slate-800 border-slate-700">
                    <SelectItem value="full" className="text-white hover:bg-slate-700">
                      <span className="flex items-center gap-2">
                        <Crown className="w-4 h-4 text-emerald-500" />
                        الترخيص الكامل (690,000 د.ع)
                      </span>
                    </SelectItem>
                    <SelectItem value="trial" className="text-white hover:bg-slate-700">
                      <span className="flex items-center gap-2">
                        <Clock className="w-4 h-4 text-blue-500" />
                        فترة تجريبية (5 أيام)
                      </span>
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="bg-emerald-500/10 border border-emerald-500/20 rounded-lg p-3">
                <p className="text-emerald-400 text-sm">
                  الترخيص الكامل يتيح جميع الميزات
                </p>
              </div>
            </div>
          )}

          <DialogFooter className="gap-2 mt-4">
            <Button
              variant="outline"
              onClick={() => setIsActivateOpen(false)}
              className="border-slate-600 text-slate-300"
            >
              إلغاء
            </Button>
            <Button
              onClick={activateUser}
              className="bg-emerald-600 hover:bg-emerald-700"
            >
              <CheckCircle className="w-4 h-4 me-2" />
              تفعيل
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
